import { Fragment } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { Divisor, Marca } from '../base';
import { ContenidoCuenta, MenuCuenta, type IdentidadPlataforma } from './MenuCuenta';
import { MenuPrincipal } from './MenuPrincipal';
import { claseEntradaNav, type GrupoMenu } from './navegacion';

/**
 * El encabezado de la aplicación: marca, navegación y cuenta. Uno solo para el negocio y para la
 * plataforma, con el mismo lenguaje visual y el mismo menú de pantalla angosta. Los destinos vienen
 * de fuera: cada pantalla sabe cuáles son los suyos.
 */
export function Encabezado({
  grupos,
  inicio,
  titulo,
  identidadPlataforma,
}: {
  grupos: GrupoMenu[];
  /** A dónde lleva la marca: el inicio de quien está mirando. */
  inicio: string;
  /** Nombre de la pantalla junto a la marca, cuando la navegación no lo dice sola. */
  titulo?: string;
  identidadPlataforma?: IdentidadPlataforma;
}) {
  const ubicacion = useLocation();

  // Qué sección se está viendo, para que el botón del menú angosto lo diga sin abrirlo.
  const seccionActiva =
    grupos
      .flatMap((g) => g.entradas)
      .find((e) =>
        e.ruta ? (e.fin ? ubicacion.pathname === e.ruta : ubicacion.pathname.startsWith(e.ruta)) : e.activa === true
      )?.texto ?? 'Menú';

  return (
    <header className="no-imprimir border-border bg-card sticky top-0 z-30 border-b">
      <div className="flex w-full flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
        <Link to={inicio}>
          <Marca className="text-marca" />
        </Link>
        {/* El título va entre separadores para que no se lea como otra entrada de la barra. */}
        {titulo && (
          <>
            <Divisor />
            <span className="text-titulo font-extrabold" data-e2e="encabezado-titulo">
              {titulo}
            </span>
          </>
        )}
        {/* Este separador cierra el bloque de identidad contra la navegación. Se esconde con ella:
            en pantalla angosta la barra no se dibuja y quedaría colgando sin nada detrás. */}
        <Divisor className="hidden lg:block" />
        {/* El rol y el entorno de prueba no se repiten aquí: los dice el desplegable del avatar. */}

        <nav className="hidden flex-1 flex-wrap items-center gap-x-3 gap-y-1 lg:flex">
          {grupos.map((grupo, i) => (
            <Fragment key={grupo.titulo}>
              {i > 0 && <Divisor />}
              <span className="flex flex-wrap gap-1" data-e2e="nav-grupo">
                {grupo.entradas.map((entrada) =>
                  entrada.ruta ? (
                    <NavLink
                      key={entrada.texto}
                      to={entrada.ruta}
                      end={entrada.fin}
                      data-e2e={entrada.e2e}
                      className={({ isActive }) => claseEntradaNav(isActive)}
                    >
                      {entrada.texto}
                    </NavLink>
                  ) : (
                    <button
                      key={entrada.texto}
                      onClick={entrada.alElegir}
                      data-e2e={entrada.e2e}
                      className={claseEntradaNav(entrada.activa === true)}
                    >
                      {entrada.texto}
                    </button>
                  )
                )}
              </span>
            </Fragment>
          ))}
        </nav>

        {/* El mismo bloque de cuenta en dos ubicaciones: desplegable del avatar cuando la barra
            cabe, y dentro del menú único cuando no. Nunca los dos a la vez. */}
        <div className="ml-auto hidden lg:block">
          <MenuCuenta identidadPlataforma={identidadPlataforma} />
        </div>
        <div className="ml-auto lg:hidden">
          <MenuPrincipal grupos={grupos} seccionActiva={seccionActiva}>
            {(cerrar) => <ContenidoCuenta alNavegar={cerrar} identidadPlataforma={identidadPlataforma} />}
          </MenuPrincipal>
        </div>
      </div>
    </header>
  );
}
